<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { McSegmentedControl, McSegmentedControlItem, McTooltip } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { FilterDefinition, FilterType, FilterTypeEnum, useApplyFilter } from "@/features/filters";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const {
  key: filterKey,
  label: filterLabel = "",
  groupId,
  groupLabel,
  sectionLabel,
  value: defaultValue,
  options: optionsProp = [],
  labelTooltip = false
} = props.filter;

const { t } = useI18n();

const { filtersApplied, panelSelectedFilters } = useFiltersStore();
const { applyFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const zone = props.zonePrefix || "default";

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return source?.[zone]?.[filterKey]?.value ?? defaultValue?.value ?? "";
});

const options = computed(() => optionsProp);

const onChange = (e: Event) => {
  const value = (e.target as HTMLSelectElement).value;
  const selectedOption = options.value.find((o) => o.value === value);
  if (!selectedOption) return;

  applyFilter(filterKey, {
    filterKey,
    value,
    label: selectedOption.label,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  });
};
</script>

<template>
  <mc-tooltip fit="small">
    <mc-segmented-control slot="trigger" fit="small" :value="selectedFilter" @listchange="onChange">
      <mc-segmented-control-item v-for="option in options" :key="option.value" :value="option.value">
        {{ t(option.label) }}
      </mc-segmented-control-item>
    </mc-segmented-control>
    <span v-if="labelTooltip && selectedFilter">{{ t(filterLabel) }}</span>
  </mc-tooltip>
</template>
